/**
 * Botão do painel (F1.8) — primitivo usado por todas as telas.
 *
 * Regra do contrato que este arquivo carrega (03 §3.5, linha "sem permissão" da tabela A):
 * **botão que a pessoa não pode usar não some em silêncio.** Ele aparece, indisponível, com a frase
 * que explica o porquê logo abaixo. Quem escreve a frase é `porQueNaoPode()` de `auth/sessao.ts`;
 * aqui só garantimos que ela é mostrada e lida.
 *
 * Por que `aria-disabled` em vez do `disabled` do HTML: botão `disabled` sai da ordem de tabulação e
 * some para o leitor de tela — quem navega por teclado nunca chegaria à explicação. Com
 * `aria-disabled` o botão continua alcançável, é anunciado como indisponível, e a frase vai junto
 * pelo `aria-describedby`. O clique é bloqueado no código, não pelo navegador.
 *
 * Hierarquia (03 §3.5 regra i — "uma tela nunca tem dois botões de mesmo peso"): só a ação principal
 * da tela usa `variante="principal"`. Todo o resto é `secundario`, `texto` ou `perigo`.
 */
import type { ButtonHTMLAttributes, CSSProperties, MouseEvent, ReactNode } from 'react';
import { useId } from 'react';
import { ESTADOS } from '../estados/tipos.js';

export type VarianteBotao = 'principal' | 'secundario' | 'texto' | 'perigo';

export interface BotaoProps extends Omit<
  ButtonHTMLAttributes<HTMLButtonElement>,
  'disabled' | 'aria-disabled' | 'children'
> {
  children: ReactNode;
  variante?: VarianteBotao | undefined;
  /**
   * Frase pronta de `porQueNaoPode()`. Preenchida = botão indisponível + explicação visível.
   * `null` (padrão) = a pessoa pode agir.
   */
  motivoDesabilitado?: string | null | undefined;
  /** Estado "ação em andamento" (tabela A): só a repetição da mesma ação fica bloqueada. */
  carregando?: boolean | undefined;
  /** Texto mostrado enquanto carrega. Padrão: o texto único do estado "ação em andamento". */
  textoCarregando?: string | undefined;
  larguraTotal?: boolean | undefined;
}

/** Cores por variante — todas saem dos tokens; nenhum literal de cor mora aqui. */
const CORES: Record<VarianteBotao, CSSProperties> = {
  principal: {
    // Não existe token "texto sobre a marca"; a superfície branca é o token que cumpre esse papel.
    background: 'var(--cor-marca)',
    color: 'var(--cor-superficie)',
    border: '1px solid var(--cor-marca-escura)',
  },
  secundario: {
    background: 'var(--cor-superficie)',
    color: 'var(--cor-texto)',
    border: '1px solid var(--cor-linha-forte)',
  },
  texto: {
    background: 'transparent',
    color: 'var(--cor-marca-escura)',
    border: '1px solid transparent',
  },
  perigo: {
    background: 'var(--cor-perigo)',
    color: 'var(--cor-superficie)',
    border: '1px solid var(--cor-perigo)',
  },
};

/** Aparência de indisponível: cinza dos tokens, para ler como "existe, mas não agora". */
const CORES_INDISPONIVEL: CSSProperties = {
  background: 'var(--cor-superficie-3)',
  color: 'var(--cor-texto-suave-2)',
  border: '1px solid var(--cor-linha)',
};

export function Botao({
  children,
  variante = 'secundario',
  motivoDesabilitado = null,
  carregando = false,
  textoCarregando,
  larguraTotal = false,
  onClick,
  style,
  type = 'button',
  ...resto
}: BotaoProps) {
  const idGerado = useId();
  const idMotivo = `botao-motivo-${idGerado}`;

  const semPermissao = typeof motivoDesabilitado === 'string' && motivoDesabilitado.trim() !== '';
  const bloqueado = semPermissao || carregando;

  // A frase do porquê entra **junto** com a descrição que a tela já tenha dado ao botão; trocar uma
  // pela outra faria o leitor de tela perder um dos dois lados da história.
  const descricao = [semPermissao ? idMotivo : null, resto['aria-describedby'] ?? null]
    .filter((parte): parte is string => typeof parte === 'string' && parte.trim() !== '')
    .join(' ');

  const estilo: CSSProperties = {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 'var(--esp-2)',
    minHeight: 'var(--alvo-toque-min)',
    padding: '0 var(--esp-4)',
    borderRadius: 'var(--raio-md)',
    fontSize: 'var(--font-size-formulario)',
    fontWeight: 'var(--peso-medio)',
    lineHeight: 'var(--altura-linha-compacta)',
    textAlign: 'center',
    cursor: bloqueado ? 'not-allowed' : 'pointer',
    transition: `background var(--duracao-rapida) var(--curva), color var(--duracao-rapida) var(--curva)`,
    width: larguraTotal ? '100%' : 'auto',
    ...(bloqueado ? CORES_INDISPONIVEL : CORES[variante]),
    ...style,
  };

  function aoClicar(evento: MouseEvent<HTMLButtonElement>) {
    // Bloquear no código (e não com `disabled`) é o que permite manter o botão focalizável.
    if (bloqueado) {
      evento.preventDefault();
      evento.stopPropagation();
      return;
    }
    onClick?.(evento);
  }

  return (
    <span
      style={{
        display: larguraTotal ? 'flex' : 'inline-flex',
        flexDirection: 'column',
        gap: 'var(--esp-1)',
        width: larguraTotal ? '100%' : 'auto',
      }}
    >
      <button
        {...resto}
        type={type}
        style={estilo}
        onClick={aoClicar}
        aria-disabled={bloqueado || undefined}
        aria-busy={carregando || undefined}
        aria-describedby={descricao === '' ? undefined : descricao}
        // A hierarquia da regra i ("uma tela nunca tem dois botões de mesmo peso") só é verificável
        // se o peso aparecer no DOM: é por aqui que o teste de cada tela conta os destaques.
        data-variante={variante}
        data-indisponivel={bloqueado ? 'sim' : undefined}
      >
        {carregando ? (textoCarregando ?? ESTADOS.acao_em_andamento.texto) : children}
      </button>
      {semPermissao ? (
        <span
          id={idMotivo}
          style={{
            color: 'var(--cor-texto-suave)',
            fontSize: 'var(--font-size-formulario)',
            lineHeight: 'var(--altura-linha-normal)',
          }}
        >
          {motivoDesabilitado}
        </span>
      ) : null}
    </span>
  );
}
